/**
 * Turn "when interviewers are collectively free" + "busy events on
 * their calendars" into a list of bookable `AvailableSlot`s.
 *
 *   availability  = [ 09:00 – 17:00 ]
 *   busy events   = [ 10:00 – 10:30, 13:00 – 14:00 ]
 *   bookable gaps = [ 09:00 – 10:00, 10:30 – 13:00, 14:00 – 17:00 ]
 *
 * Rules:
 *   - Slots entirely in the past are skipped.
 *   - If a day is fully covered by a single busy event, skip it.
 *   - Gaps shorter than `minSlotMinutes` are dropped.
 *   - Multi-day availability windows are split and processed per day.
 *
 * The algorithm per (availability slot):
 *   1. Collect busy events overlapping the slot and clip them to it.
 *   2. Merge those busy pieces.
 *   3. Walk `slotStart → slotEnd`, emitting a slot for each gap.
 */
import moment, { type Moment } from 'moment-timezone';
import { v4 as uuid } from 'uuid';
import type { AvailableSlot, BusyEvent, Interval } from './types';
import { splitIntervalByDay } from './splitByDay';

const MS_PER_MINUTE = 60_000;

export interface SubtractBusyArgs {
  availability: Interval[];
  busyEvents: BusyEvent[];
  timezone: string;
  /** Smallest gap we'd emit as a slot. Default: 15 min. */
  minSlotMinutes?: number;
}

export function subtractBusyFromAvailability({
  availability,
  busyEvents,
  timezone,
  minSlotMinutes = 15,
}: SubtractBusyArgs): AvailableSlot[] {
  if (availability.length === 0) {
    return [];
  }

  const now = moment().tz(timezone);
  const minMs = minSlotMinutes * MS_PER_MINUTE;
  const busyByDay = groupBusyByDay(busyEvents, timezone);

  const slots: AvailableSlot[] = [];
  for (const window of availability) {
    const dayPieces = splitIntervalByDay(window, timezone);
    const crossesMidnight = dayPieces.length > 1;
    for (const piece of dayPieces) {
      emitSlotsForDay({
        piece,
        now,
        minMs,
        timezone,
        busyForDay: busyByDay[piece.dateKey] ?? [],
        crossesMidnight,
        out: slots,
      });
    }
  }
  return slots;
}

function groupBusyByDay(
  busyEvents: BusyEvent[],
  timezone: string
): Record<string, { start: Moment; end: Moment }[]> {
  const byDay: Record<string, { start: Moment; end: Moment }[]> = {};
  for (const evt of busyEvents) {
    for (const piece of splitIntervalByDay(evt, timezone)) {
      const list = byDay[piece.dateKey] ?? (byDay[piece.dateKey] = []);
      list.push({
        start: moment.tz(piece.start, timezone),
        end: moment.tz(piece.end, timezone),
      });
    }
  }
  return byDay;
}

interface EmitArgs {
  piece: { dateKey: string; start: string; end: string };
  now: Moment;
  minMs: number;
  timezone: string;
  busyForDay: { start: Moment; end: Moment }[];
  crossesMidnight: boolean;
  out: AvailableSlot[];
}

function emitSlotsForDay({
  piece,
  now,
  minMs,
  timezone,
  busyForDay,
  crossesMidnight,
  out,
}: EmitArgs) {
  const pieceStart = moment.tz(piece.start, timezone);
  const pieceEnd = moment.tz(piece.end, timezone);
  if (pieceEnd.isSameOrBefore(now)) {
    return;
  }

  // Never book in the past — round "now" up to the next 15-min grid
  // so slots feel tidy.
  const effectiveStart = pieceStart.isSame(now, 'day')
    ? moment.max(pieceStart, roundUpToQuarterHour(now))
    : pieceStart;
  if (effectiveStart.isSameOrAfter(pieceEnd)) {
    return;
  }

  // A busy event that fully covers this day chunk → nothing to offer.
  const fullyBusy = busyForDay.some(
    (b) => b.start.isSameOrBefore(pieceStart) && b.end.isSameOrAfter(pieceEnd)
  );
  if (fullyBusy) {
    return;
  }

  // Clip busy events to this availability piece and merge.
  const clipped = busyForDay
    .filter((b) => b.start.isBefore(pieceEnd) && b.end.isAfter(pieceStart))
    .map((b) => ({
      start: moment.max(b.start, effectiveStart),
      end: moment.min(b.end, pieceEnd),
    }))
    .filter((b) => b.end.isAfter(b.start))
    .sort((a, b) => a.start.valueOf() - b.start.valueOf());

  const mergedBusy: { start: Moment; end: Moment }[] = [];
  for (const b of clipped) {
    const last = mergedBusy[mergedBusy.length - 1];
    if (last && b.start.isSameOrBefore(last.end)) {
      if (b.end.isAfter(last.end)) last.end = b.end;
    } else {
      mergedBusy.push(b);
    }
  }

  // Walk gap → emit, busy → skip.
  let cursor = effectiveStart;
  for (const b of mergedBusy) {
    if (b.start.isAfter(cursor)) {
      pushSlotIfLongEnough(
        cursor,
        b.start,
        minMs,
        piece.dateKey,
        crossesMidnight,
        out
      );
    }
    if (b.end.isAfter(cursor)) {
      cursor = b.end;
    }
  }
  if (cursor.isBefore(pieceEnd)) {
    pushSlotIfLongEnough(
      cursor,
      pieceEnd,
      minMs,
      piece.dateKey,
      crossesMidnight,
      out
    );
  }
}

function pushSlotIfLongEnough(
  start: Moment,
  end: Moment,
  minMs: number,
  dateKey: string,
  crossesMidnight: boolean,
  out: AvailableSlot[]
) {
  if (end.diff(start) < minMs) {
    return;
  }
  out.push({
    id: uuid(),
    dateKey,
    start: start.toISOString(),
    end: end.toISOString(),
    crossesMidnight,
  });
}

function roundUpToQuarterHour(m: Moment): Moment {
  const minutes = m.minutes();
  const up = Math.ceil(minutes / 15) * 15;
  return m.clone().minutes(up).seconds(0).milliseconds(0);
}
